import React from "react";
import { createPortal } from "react-dom";
import { assets } from "../../Assets/assets";

const AppLoader = ({
  fullScreen = true,
  blocking = false,
  message = "Loading, please wait...",
  showLogo = true
}) => {

  // Non-blocking loader — transparent background matching all other screens perfectly
  const NormalLoader = () => (
    <div
      className={`flex items-center justify-center ${
        fullScreen ? "min-h-[70vh] w-full" : "w-full py-12"
      } bg-transparent text-gray-800 dark:text-gray-200 transition-colors duration-200`}
    >
      <LoaderContent message={message} showLogo={showLogo} />
    </div>
  );

  // Blocking loader with portal overlay — transparent card container over dark backdrop
  const BlockingLoader = () =>
    createPortal(
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-xs animate-fadeIn"
      >
        <div className="relative p-6 sm:p-8 flex flex-col items-center justify-center animate-scaleIn bg-transparent">
          <LoaderContent message={message} showLogo={showLogo} enhanced isDarkOverlay />
        </div>
      </div>,
      document.body
    );

  return blocking ? <BlockingLoader /> : <NormalLoader />;
};

// Shared loader content component
const LoaderContent = ({ message, showLogo, enhanced = false, isDarkOverlay = false }) => {
  return (
    <div className="flex flex-col items-center gap-5 text-center bg-transparent">

      {/* Animated Loader */}
      <div className="relative w-20 h-20 flex items-center justify-center">

        {/* Outer rotating ring */}
        <div className="absolute inset-0 rounded-full border-4 border-primaryColor/30 animate-spin-slow"></div>

        {/* Middle pulsing ring */}
        <div className="absolute inset-2 rounded-full border-4 border-subPrimaryColor/40 animate-ping-slow"></div>

        {/* Inner fast spinning ring */}
        <div className="absolute inset-4 rounded-full border-4 border-t-primaryColor border-b-transparent border-l-transparent animate-spin"></div>

        {/* Pulse background */}
        {enhanced && (
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primaryColor/20 to-subPrimaryColor/20 animate-ping opacity-60"></div>
        )}

        {/* Logo centered */}
        {showLogo && (
          <div className="relative z-10 flex items-center justify-center">
            <img
              src={assets.homeactive}
              alt="logo"
              className="h-10 w-10 object-contain animate-pulse-subtle drop-shadow-md"
            />
          </div>
        )}
      </div>

      {/* Text with animated dots */}
      <div className="flex flex-col items-center gap-2 max-w-xs">
        <p
          className={`text-sm sm:text-base font-semibold tracking-wide ${
            isDarkOverlay
              ? "text-white drop-shadow-md"
              : "text-gray-800 dark:text-gray-200"
          }`}
        >
          {message}
        </p>

        {/* Animated loading dots */}
        <div className="flex items-center gap-1.5 pt-1">
          <span
            className="w-2.5 h-2.5 bg-primaryColor rounded-full animate-bounce shadow-xs"
            style={{ animationDelay: "0ms" }}
          ></span>
          <span
            className="w-2.5 h-2.5 bg-subPrimaryColor rounded-full animate-bounce shadow-xs"
            style={{ animationDelay: "150ms" }}
          ></span>
          <span
            className="w-2.5 h-2.5 bg-primaryColor rounded-full animate-bounce shadow-xs"
            style={{ animationDelay: "300ms" }}
          ></span>
        </div>
      </div>
    </div>
  );
};

export default AppLoader;